import { duration } from '@eturi/date-util'
import type { SetRequired } from '@eturi/ts-helpers'
import { arrayAddOrUpdate, assertNotNullish, pick, setIfNotEqual } from '@eturi/util'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'
import reject from 'lodash/reject.js'
import { createSliceTransformer } from 'rtk-slice-transformer'
import { removeDeviceFromAllStores, resetAction } from '../actions/index.js'
import { bindCreateAsyncThunkToState } from '../bindCreateAsyncThunkToState.js'
import type { HttpExtra } from '../http.js'
import { isManagedDevice } from '../model-util/device.js'
import type {
	CommandReqBody,
	InitState,
	NewAccountDevice,
	RawDevice,
	RawManagedDevice,
	SThunkState,
} from '../types/index.js'
import { toBoolAction } from '../util/index.js'

export type DeviceState = InitState & {
	readonly androidFcmToken: Maybe<string>
	readonly deviceInfoFetchTs?: number
	readonly devices: RawDevice[]
}

export type WithDeviceState = {
	readonly device: DeviceState
}

const initialState: DeviceState = {
	androidFcmToken: null,
	devices: [],
	isInit: false,
}

export const deviceSlice = /*@__PURE__*/ createSlice({
	name: 'device',
	initialState,
	reducers: {
		setAndroidFcmToken(s, a: PayloadAction<Maybe<string>>) {
			s.androidFcmToken = a.payload
		},
	},
	extraReducers: (builder) =>
		builder
			.addCase(resetAction, () => initialState)
			.addCase(fetchDevices.fulfilled, (s, a) => {
				s.isInit = true
				setIfNotEqual(s, 'devices', a.payload)
			})
			.addCase(removeDeviceFromAllStores, (s, a) => {
				const devices = reject(s.devices, { device_id: a.payload })

				if (devices.length !== s.devices.length) s.devices = devices
			})
			.addCase(requestDeviceInfo.fulfilled, (s) => {
				s.deviceInfoFetchTs = Date.now()
			})
			.addMatcher(
				isAnyOf(createDevice.fulfilled, fetchDevice.fulfilled, updateDevice.fulfilled),
				(s, a) => {
					s.devices = arrayAddOrUpdate(s.devices, a.payload, 'device_id')
				},
			),
})

export const { setAndroidFcmToken } = deviceSlice.actions

export const deviceSliceTransformer = /*@__PURE__*/ createSliceTransformer(
	deviceSlice,
	(s) => ({
		isInit: s.isInit,
		devices: s.devices
			.filter((d) => !d.retired)
			.map((d) =>
				pick(d, [
					'app',
					'app_version',
					'command_state',
					'created',
					'device_id',
					'device_location_active',
					'device_location_ts',
					'display_name',
					'display_state',
					'last_activity',
					'last_seen',
					'make',
					'management_level',
					'management_removed',
					'model',
					'model_name',
					'os',
					'os_version',
					'pair_id',
					'schedule_ts',
					'suspended',
					'system_components',
					'users',
					'version',
				]),
			),
	}),
	(a) => (setAndroidFcmToken.match(a) ? toBoolAction(a) : a),
)

////////// Thunks //////////////////////////////////////////////////////////////

export type DeviceThunkState = SThunkState & WithDeviceState

const createAsyncThunk = /*@__PURE__*/ bindCreateAsyncThunkToState<DeviceThunkState>()

// FIXME: Handle errors since this used to try / catch
export const createDevice = /*@__PURE__*/ createAsyncThunk(
	'device/create',
	async (device: NewAccountDevice, { dispatch, extra: { http } }) => {
		const newDevice = await dispatch(http.post<Maybe<RawDevice>>('/device', { data: [device] }))

		assertNotNullish(newDevice, 'Create RawDevice')

		return newDevice
	},
)

// FIXME: try / catch client impl
export const fetchDevice = /*@__PURE__*/ createAsyncThunk(
	'device/fetch',
	async (deviceId: string, { dispatch, extra: { http } }) => {
		const res = await dispatch(http.get<Maybe<[RawDevice]>>(`/device?device_id=${deviceId}`))
		const device = res?.[0]

		assertNotNullish(device, 'RawDevice')

		return device
	},
)

export const fetchDevices = /*@__PURE__*/ createAsyncThunk(
	'device/fetchAll',
	async (extra: HttpExtra = {}, { dispatch, extra: { http } }) => {
		const devices = await dispatch(http.get<Maybe<RawDevice[]>>('/device', extra))

		assertNotNullish(devices, 'RawDevice[]')

		return devices
	},
	{
		condition: (arg, api) => {
			if (!arg?.force && isDevicesInit$(api.getState())) return false
		},
	},
)

export const removeDevice = /*@__PURE__*/ createAsyncThunk(
	'device/delete',
	async (device_id: string, { dispatch, extra: { http } }) => {
		const res = await dispatch(
			http.delete<Maybe<{ objects_deleted: number }>>('/device', { data: { device_id } }),
		)

		const nDeleted = res?.objects_deleted || 0

		if (nDeleted) {
			dispatch(removeDeviceFromAllStores(device_id))

			await dispatch(fetchDevices({ force: true })).unwrap()

			return true
		}

		return false
	},
)

/**
 * Posts a command for the server to query devices for attributes. Note that, while this can take
 * a user_id, it currently doesn't, because none of the screens where we use (for battery), are
 * single user screens. If we ever need user-level control of this call, we'll want need to create
 * a fetch ts map, or some other implementation. Also note that, while this is technically a
 * "command", and should therefore belong in `rule.slice`, it does not affect the rule model in
 * any way. It only affects the device model (attributes).
 */
export const requestDeviceInfo = /*@__PURE__*/ createAsyncThunk(
	'device/requestDeviceInfo',
	async (extra: HttpExtra = {}, { dispatch, extra: { http } }) => {
		await dispatch(
			http.post('/command', {
				...extra,
				data: { command: 'device_info' } satisfies CommandReqBody,
			}),
		)
	},
	{
		condition(arg, api) {
			// Make the device info call, at most, once every 6 minutes, unless forced
			if (
				arg?.force !== true &&
				Date.now() - (api.getState().device.deviceInfoFetchTs || 0) < duration(6, 'm')
			) {
				return false
			}
		},
	},
)

type UpdateDevicePayload = SetRequired<Partial<RawDevice>, 'device_id'>

export const updateDevice = /*@__PURE__*/ createAsyncThunk(
	'device/update',
	async (update: UpdateDevicePayload, { dispatch, extra: { http } }) => {
		const device = await dispatch(http.put<Maybe<RawDevice>>('/device', { data: [update] }))

		assertNotNullish(device, 'RawDevice')

		return device
	},
)

// NOTE: Not implemented in OurPact yet
// export const reassignDeviceAction;

////////// Selectors ///////////////////////////////////////////////////////////

const state$ = <T extends WithDeviceState>(s: T) => s.device

export const androidFcmToken$ = /*@__PURE__*/ createSelector(state$, (d) => d.androidFcmToken)
export const rawDevices$ = /*@__PURE__*/ createSelector(state$, (s) => s.devices)
export const rawManagedDevices$ = /*@__PURE__*/ createSelector(
	rawDevices$,
	(d): RawManagedDevice[] => d.filter(isManagedDevice),
)
export const isDevicesInit$ = /*@__PURE__*/ createSelector(state$, (s) => s.isInit)
